import { isDefined, toError } from "@clipboard-health/util-ts";
import { setAuthHeaderProvider } from "@src/appV2/Auth/cognito/authHeaderProviderRef";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { FEATURE_FLAG_DEFAULT_VALUES } from "@src/appV2/FeatureFlags/constants";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
// eslint-disable-next-line no-restricted-imports
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
// eslint-disable-next-line no-restricted-imports
import { getFirebaseSingleton } from "@src/lib/firebase/src";
import constate from "constate";
import { useCallback, useEffect, useRef } from "react";

const BEARER_TOKEN_PREFIX = "Bearer ";
const MAX_COGNITO_ERROR_LOGS = 5;

/**
 * Hook that provides centralized authentication header values for API calls.
 * Manages the migration from Firebase to Cognito authentication based on feature flags.
 *
 * Returns values ready for the Authorization header:
 * - **Cognito** (both flags enabled): `Bearer <token>` (OAuth 2.0 standard)
 * - **Firebase** (fallback): `<token>` (legacy format)
 *
 * Implements rate-limited logging to prevent log flooding.
 *
 * @returns An object with getAuthHeaderValue function that returns the formatted auth value
 */
function useAuthHeaderProvider() {
  const useCognitoAuthForApi = useCbhFlag(CbhFeatureFlag.ENABLE_COGNITO_IN_API_CALLS, {
    defaultValue: FEATURE_FLAG_DEFAULT_VALUES[CbhFeatureFlag.ENABLE_COGNITO_IN_API_CALLS],
  });

  const isCognitoSyncEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_COGNITO_AUTH_SYNC, {
    defaultValue: FEATURE_FLAG_DEFAULT_VALUES[CbhFeatureFlag.ENABLE_COGNITO_AUTH_SYNC],
  });

  // Track what we've already logged this session to prevent log flooding
  const loggedEvents = useRef({
    tokenUnavailable: false,
    flagsPartiallyEnabled: false,
    errorCount: 0,
  });

  const getAuthHeaderValue = useCallback(
    async (forceRefresh?: boolean): Promise<string | undefined> => {
      if (useCognitoAuthForApi && isCognitoSyncEnabled) {
        try {
          const cognitoToken = await getCognitoSingleton().getIdToken(forceRefresh);

          if (isDefined(cognitoToken)) {
            return `${BEARER_TOKEN_PREFIX}${cognitoToken}`;
          }

          if (!loggedEvents.current.tokenUnavailable) {
            logEvent(APP_V2_APP_EVENTS.COGNITO_TOKEN_FALLBACK_TO_FIREBASE, {
              reason: "token_unavailable",
              useCognitoAuthForApi,
              isCognitoSyncEnabled,
            });
            loggedEvents.current.tokenUnavailable = true;
          }
        } catch (maybeError: unknown) {
          if (loggedEvents.current.errorCount < MAX_COGNITO_ERROR_LOGS) {
            logError(APP_V2_APP_EVENTS.FAILED_TO_GET_COGNITO_TOKEN, {
              error: toError(maybeError),
            });
            loggedEvents.current.errorCount += 1;
          }
        }
      } else if (
        (useCognitoAuthForApi || isCognitoSyncEnabled) &&
        !loggedEvents.current.flagsPartiallyEnabled
      ) {
        logEvent(APP_V2_APP_EVENTS.COGNITO_TOKEN_FALLBACK_TO_FIREBASE, {
          reason: "flags_partially_enabled",
          useCognitoAuthForApi,
          isCognitoSyncEnabled,
        });
        loggedEvents.current.flagsPartiallyEnabled = true;
      }

      // Firebase fallback (legacy format without Bearer prefix)
      return await getFirebaseSingleton().currentUser?.getIdToken(forceRefresh);
    },
    [useCognitoAuthForApi, isCognitoSyncEnabled]
  );

  useEffect(() => {
    setAuthHeaderProvider(getAuthHeaderValue);
    return () => {
      setAuthHeaderProvider(undefined);
    };
  }, [getAuthHeaderValue]);

  return { getAuthHeaderValue };
}

export const [AuthHeaderProvider, useAuthHeaderContext] = constate(useAuthHeaderProvider);
